// src/client/components/profile-manager/detail-view/InlineEditField.tsx

import { useState, useRef, useEffect } from 'react';
import { Input } from '@client/components/ui/input';

interface InlineEditFieldProps {
  label: string;
  value: string | number | null;
  onSave: (value: string | number | null) => Promise<void>;
  type?: 'text' | 'number' | 'email';
  placeholder?: string;
}

export default function InlineEditField({
  label,
  value,
  onSave,
  type = 'text',
  placeholder = 'Click to edit...',
}: InlineEditFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value?.toString() || '');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    setEditValue(value?.toString() || '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (editValue === (value?.toString() || '')) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      const finalValue = type === 'number' 
        ? (editValue ? parseFloat(editValue) : null)
        : (editValue || null);
      
      await onSave(finalValue);
      setIsEditing(false);
    } catch (error) {
      console.error('[InlineEditField] Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value?.toString() || '');
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleBlur = () => {
    handleCancel();
  };

  // ✅ CRITICAL: Safe display value
  const displayValue = value !== null && value !== undefined ? String(value) : '';

  return (
    <div className="flex items-center py-2 border-b">
      <label className="w-32 text-sm font-medium text-gray-700">
        {label}:
      </label>
      <div className="flex-1">
        {isEditing ? (
          <Input
            ref={inputRef}
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            disabled={isSaving}
            placeholder={placeholder}
            className="h-8"
          />
        ) : (
          <div
            onClick={handleClick}
            className="cursor-pointer hover:bg-gray-50 px-2 py-1 rounded min-h-[2rem] flex items-center"
          >
            {displayValue || (
              <span className="text-gray-400 italic">{placeholder}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}